import axios from "axios";
import { dropEmptyKeys } from "../core";
import { isSessionExpired, refreshCsrfToken } from "../csrf-token-manager";
import getMetaEnv from "../meta";
import { asyncRetryIfTransientError, fetchWithRetry } from "../retries";
import isSameOriginRequest from "../same-origin";
import { withCache } from "./cache";
import buildRequestFingerprint from "./build-request-fingerprint";
import { isAxiosNetworkError, shouldReportError } from "./error-classification";
import { buildNetworkConnectionError, reportRequestError } from "./error-reporting";

export { isAxiosNetworkError, shouldReportError, buildRequestFingerprint };

const withCsrfRetry = async (requestFn, source) => {
  try {
    return await requestFn();
  } catch (error) {
    if (!isSessionExpired(error)) throw error;

    const refreshed = await refreshCsrfToken(source);
    if (!refreshed) {
      throw error;
    }

    return requestFn();
  }
};

const AXIOS_DEFAULT_TIMEOUT = 15_000;

const MUTATION_METHODS = ["POST", "PUT", "PATCH", "DELETE"];

// MN-8803: status-only transient-retry predicate. Does NOT match network/timeout
// messages (isTransientError does) — raw network failures are already retried by
// fetchWithRetry inside executeFetch, so matching them here would double-retry.
const isTransientStatus = error =>
  [502, 503, 504].includes(error?.status ?? error?.response?.status);

const defaultHeaders = () => ({
  "X-CSRF-Token": getMetaEnv("csrf-token"),
  "X-Requested-With": "XMLHttpRequest",
  Accept: "application/json",
  "Content-Type": "application/json; charset=utf-8"
});

const buildParams = ({ headers = {}, ...rest }, method) => ({
  method,
  headers: dropEmptyKeys({
    ...defaultHeaders(),
    ...headers
  }),
  credentials: "same-origin",
  ...rest
});

const haltErrorResponse = async response => {
  if (response.status >= 400 && response.status <= 599) {
    let errorMessage;
    let responseData;
    const textBody = await response.text();

    // Try to parse JSON response body
    try {
      responseData = JSON.parse(textBody);
    } catch {
      responseData = textBody;
    }

    if (textBody) {
      errorMessage =
        response.status === 422
          ? textBody
          : `${response.statusText || `HTTP ${response.status}`}: ${textBody}`;
    } else {
      errorMessage = response.statusText || `HTTP ${response.status}`;
    }
    const error = new Error(errorMessage);
    error.status = response.status;
    error.message = errorMessage;
    error.response = responseData; // Preserve the parsed response data
    throw error;
  }
  return response;
};

const increaseAsyncRequestsCount = url => {
  if (isSameOriginRequest(url)) window.ajax_active += 1;
};

const decreaseAsyncRequestsCount = url => {
  if (isSameOriginRequest(url)) window.ajax_active -= 1;
};

const sendBeacon = (url, data) =>
  new Promise((resolve, reject) => {
    const dataString = JSON.stringify(data);
    const queued = navigator.sendBeacon(url, dataString);

    if (queued) {
      resolve();
    } else {
      reject(new Error(`Failed to queue data for sending to ${url}`));
    }
  });

const executeFetch = (url, params, method) =>
  fetchWithRetry(url, buildParams(params, method))
    .then(haltErrorResponse)
    .then(async response => {
      if (response.status === 204) return undefined;
      const text = await response.text();
      if (!text) return undefined;
      try {
        return JSON.parse(text);
      } catch (parseError) {
        const error = new Error(`Non-JSON response for ${method} ${url}: ${text.slice(0, 80)}`);
        error.status = response.status;
        error.response = text;
        error.cause = parseError;
        throw error;
      }
    });

// `silent: true` opt-in skips notifyError() for known-noisy probes/polls
// (e.g. DTF artwork enhancement acknowledgement, jiffystickers composition
// lookup). The error is still rethrown so caller .catch handlers run; only
// the Honeybadger notify pipe is suppressed. See HB-1851.
//
// `noRetry: true` opt-in skips the transient-5xx GET retry (mirrors axiosRequest).
const request = (url, params = {}, method = "GET") => {
  const { silent, noRetry, ...rest } = params;
  increaseAsyncRequestsCount(url);

  if (rest.sendBeacon) {
    delete rest.sendBeacon;
    decreaseAsyncRequestsCount(url);
    return sendBeacon(url, rest);
  }

  const upperMethod = method.toUpperCase();
  const execute = () => executeFetch(url, rest, method);

  // MN-8803: ONE retry of transient gateway 5xx (502/503/504 — Cloudflare/Envoy
  // challenge pages during pod scaling) for idempotent GETs. Status-only errorMatch
  // keeps this off the network layer that fetchWithRetry already covers; remainingTries:1
  // (single retry, per the ticket) bounds the inner×outer attempt fan-out.
  const runFetch = () =>
    upperMethod === "GET" && !noRetry
      ? asyncRetryIfTransientError(execute, { errorMatch: isTransientStatus, remainingTries: 1 })
      : execute();

  const fetchPromise = MUTATION_METHODS.includes(upperMethod)
    ? withCsrfRetry(execute, "request_layer")
    : runFetch();

  return fetchPromise
    .catch(error => {
      reportRequestError(error, { url, method, responseBody: error.response, silent });
      // Only convert to the network sentinel when there is no HTTP status (mirrors
      // shouldReportError's guard to avoid swallowing server error bodies). The helper
      // preserves the original via cause + a sub-type without re-reporting it.
      const httpStatus = error.status ?? error.response?.status;
      if (!httpStatus && isAxiosNetworkError(error)) {
        throw buildNetworkConnectionError(error);
      }
      throw error;
    })
    .finally(() => decreaseAsyncRequestsCount(url));
};

/**
 * A non-native implementation of request method
 * You should use default export unless you need specific functions like progress tracking
 * @param url
 * @param params
 * @param method
 * @returns {Promise<void>|Promise<unknown>}
 */
const executeAxios = (url, params, method) => {
  const { timeout, ...axiosParams } = buildParams(params, method);
  return axios(url, {
    ...axiosParams,
    timeout: timeout ?? AXIOS_DEFAULT_TIMEOUT
  })
    .then(haltErrorResponse)
    .then(response => response.data);
};

export const axiosRequest = (url, params = {}, method = "GET") => {
  increaseAsyncRequestsCount(url);
  const { noRetry, silent, ...requestParams } = params;

  if (requestParams.sendBeacon) {
    delete requestParams.sendBeacon;
    decreaseAsyncRequestsCount(url);
    return sendBeacon(url, requestParams);
  }

  const upperMethod = method.toUpperCase();
  const isGet = upperMethod === "GET";
  const execute = () =>
    MUTATION_METHODS.includes(upperMethod)
      ? withCsrfRetry(() => executeAxios(url, requestParams, method), "axios_request_layer")
      : executeAxios(url, requestParams, method);

  const shouldRetry = isGet && !noRetry;
  const axiosPromise = shouldRetry
    ? asyncRetryIfTransientError(execute, { errorMatch: isAxiosNetworkError })
    : execute();

  return axiosPromise
    .catch(error => {
      reportRequestError(error, { url, method, responseBody: error.response?.data, silent });
      throw error;
    })
    .finally(() => decreaseAsyncRequestsCount(url));
};

export default withCache(request);
